import { useEffect, useMemo, useState } from 'react';
import { Table, Spin, Select, Input, Typography, Pagination, Button } from 'antd';
import { CheckOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../service/api';

const { Title } = Typography;

const MESES = [
  { key: 'jan', label: 'Jan' }, { key: 'fev', label: 'Fev' }, { key: 'mar', label: 'Mar' },
  { key: 'abr', label: 'Abr' }, { key: 'mai', label: 'Mai' }, { key: 'jun', label: 'Jun' },
  { key: 'jul', label: 'Jul' }, { key: 'ago', label: 'Ago' }, { key: 'set', label: 'Set' },
  { key: 'out', label: 'Out' }, { key: 'nov', label: 'Nov' }, { key: 'dez', label: 'Dez' },
];

const anoAtual = new Date().getFullYear();
const ANOS = Array.from({ length: 6 }, (_, i) => anoAtual - i);

// OR dos meses entre os filhos: um nivel-pai "tem" no mes se qualquer descendente teve extrato.
function agregarMeses(filhos) {
  const meses = {};
  MESES.forEach(({ key }) => { meses[key] = filhos.some((f) => f[key]); });
  return meses;
}

function rotuloVazio(valor, alternativa) {
  const v = (valor ?? '').toString().trim();
  return v === '' ? alternativa : v;
}

// Monta a arvore Grupo > Empresa > Banco > Conta a partir das linhas planas (ja paginadas/filtradas
// pelo backend). A ordem de insercao (Map) preserva a ordenacao que vem do SQL.
function montarArvore(linhas) {
  const grupos = new Map();

  linhas.forEach((linha) => {
    const gKey = rotuloVazio(linha.grupo, '(sem grupo)');
    const eKey = rotuloVazio(linha.empresa, '(sem empresa)');
    const bKey = rotuloVazio(linha.banco, '(sem banco)');
    const cKey = rotuloVazio(linha.conta, '(sem conta)');

    if (!grupos.has(gKey)) grupos.set(gKey, { rotulo: gKey, empresas: new Map() });
    const grupo = grupos.get(gKey);

    if (!grupo.empresas.has(eKey)) grupo.empresas.set(eKey, { rotulo: eKey, cnpj: linha.cnpj, bancos: new Map() });
    const empresa = grupo.empresas.get(eKey);

    if (!empresa.bancos.has(bKey)) empresa.bancos.set(bKey, { rotulo: bKey, contas: new Map() });
    const banco = empresa.bancos.get(bKey);

    if (!banco.contas.has(cKey)) {
      const folha = { rotulo: cKey, cnpj: linha.cnpj };
      MESES.forEach(({ key }) => { folha[key] = !!linha[key]; });
      banco.contas.set(cKey, folha);
    }
  });

  return Array.from(grupos.values()).map((grupo, gi) => {
    const empresas = Array.from(grupo.empresas.values()).map((empresa, ei) => {
      const bancos = Array.from(empresa.bancos.values()).map((banco, bi) => {
        const contas = Array.from(banco.contas.values()).map((conta, ci) => ({
          key: `g${gi}-e${ei}-b${bi}-c${ci}`,
          nivel: 'Conta',
          rotulo: conta.rotulo,
          cnpj: conta.cnpj,
          ...MESES.reduce((acc, { key }) => ({ ...acc, [key]: conta[key] }), {}),
        }));
        return {
          key: `g${gi}-e${ei}-b${bi}`,
          nivel: 'Banco',
          rotulo: banco.rotulo,
          children: contas,
          ...agregarMeses(contas),
        };
      });
      return {
        key: `g${gi}-e${ei}`,
        nivel: 'Empresa',
        rotulo: empresa.rotulo,
        cnpj: empresa.cnpj,
        children: bancos,
        ...agregarMeses(bancos),
      };
    });
    return {
      key: `g${gi}`,
      nivel: 'Grupo',
      rotulo: grupo.rotulo,
      children: empresas,
      ...agregarMeses(empresas),
    };
  });
}

const ExtratosPainelResumo = () => {
  const [dados, setDados] = useState([]);
  const [totalGrupos, setTotalGrupos] = useState(0);
  const [carregando, setCarregando] = useState(true);

  const [ano, setAno] = useState(anoAtual);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);
  const [busca, setBusca] = useState('');
  const [recarga, setRecarga] = useState(0);

  const antIcon = <LoadingOutlined style={{ fontSize: 34, color: '#2ce414' }} spin />;

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setCarregando(true);
      try {
        const resposta = await api.get('Extratos/resumo-anual', {
          params: { ano, pagina, tamanhoPagina, busca },
        });
        if (!ativo) return;
        setDados(resposta.data?.data ?? []);
        setTotalGrupos(resposta.data?.totalGrupos ?? 0);
      } catch (erro) {
        console.error('Erro ao carregar resumo de extratos:', erro);
        if (ativo) { setDados([]); setTotalGrupos(0); }
      } finally {
        if (ativo) setCarregando(false);
      }
    }
    carregar();
    return () => { ativo = false; };
  }, [ano, pagina, tamanhoPagina, busca, recarga]);

  const arvore = useMemo(() => montarArvore(dados), [dados]);

  const aplicarBusca = (valor) => {
    setPagina(1);
    setBusca((valor ?? '').trim());
  };

  // Refresh: rele o resumo. O backend ja mantem tabela e cache corretos a cada insert/delete de extrato,
  // entao nao precisa recalcular nada aqui (rapido, vem do cache quando nada mudou).
  const atualizarDados = () => setRecarga((r) => r + 1);

  const colunas = useMemo(() => [
    {
      title: 'Grupo / Empresa / Banco / Conta',
      dataIndex: 'rotulo',
      key: 'rotulo',
      fixed: 'left',
      width: 340,
      ellipsis: true,
      render: (valor, registro) => {
        const peso = registro.nivel === 'Grupo' ? 700 : registro.nivel === 'Empresa' ? 600 : 400;
        return <span style={{ fontWeight: peso }}>{valor}</span>;
      },
    },
    { title: 'CNPJ', dataIndex: 'cnpj', key: 'cnpj', width: 150, render: (v) => v ?? '' },
    ...MESES.map((mes) => ({
      title: mes.label,
      dataIndex: mes.key,
      key: mes.key,
      width: 60,
      align: 'center',
      render: (valor) =>
        valor ? <CheckOutlined style={{ color: '#2fae2f' }} /> : <span style={{ color: '#d9d9d9' }}>—</span>,
    })),
  ], []);

  return (
    <div style={{ padding: '8px 4px' }}>
      <Title level={4} style={{ marginBottom: 16 }}>Extratos no Painel — Resumo Anual</Title>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <span style={{ marginRight: 8 }}>Ano:</span>
          <Select
            value={ano}
            onChange={(v) => { setPagina(1); setAno(v); }}
            style={{ width: 120 }}
            options={ANOS.map((a) => ({ value: a, label: String(a) }))}
          />
        </div>
        <Input.Search
          placeholder="Buscar por grupo, empresa, CNPJ, banco ou conta"
          allowClear
          enterButton
          defaultValue={busca}
          onSearch={aplicarBusca}
          style={{ maxWidth: 420 }}
        />
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={atualizarDados}
          loading={carregando}
          title="Recarregar os dados"
        >
          Atualizar
        </Button>
      </div>

      <Spin indicator={antIcon} spinning={carregando}>
        {/* Altura fixa: a area da tabela reserva sempre a mesma altura, entao o modal nao
            encolhe/estica conforme a quantidade de linhas/filtro. */}
        <div style={{ height: 460, overflow: 'hidden' }}>
          <Table
            columns={colunas}
            dataSource={arvore}
            rowKey="key"
            size="small"
            bordered
            scroll={{ x: 'max-content', y: 400 }}
            pagination={false}
          />
        </div>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={pagina}
            pageSize={tamanhoPagina}
            total={totalGrupos}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            showTotal={(t) => `${t} grupo(s)`}
            onChange={(p, s) => { setPagina(p); setTamanhoPagina(s); }}
          />
        </div>
      </Spin>
    </div>
  );
};

export default ExtratosPainelResumo;
